import { User, Session } from '@supabase/supabase-js';

export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  created_at: string;
  organization_id?: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
    roles?: string[];
  };
  is_global_admin: boolean;
}

export enum AuthState {
  INITIALIZING = 'INITIALIZING',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  AUTHENTICATING = 'AUTHENTICATING',
  AUTHENTICATED = 'AUTHENTICATED',
  DEAUTHENTICATING = 'DEAUTHENTICATING',
  ERROR = 'ERROR',
}

export enum AuthEventType {
  LOGIN = 'LOGIN',
  LOGIN_SUCCESS = 'LOGIN_SUCCESS',
  LOGIN_FAILED = 'LOGIN_FAILED',
  LOGOUT = 'LOGOUT',
  LOGOUT_SUCCESS = 'LOGOUT_SUCCESS',
}

export type AuthEvent =
  | { type: AuthEventType.LOGIN; payload: { email: string; password: string } }
  | {
      type: AuthEventType.LOGIN_SUCCESS;
      payload?: { user?: User; profile?: UserProfile; session?: Session };
    }
  | { type: AuthEventType.LOGIN_FAILED; payload?: { error?: string } }
  | { type: AuthEventType.LOGOUT; payload?: { scope?: 'global' | 'local' } }
  | { type: AuthEventType.LOGOUT_SUCCESS; payload?: undefined };

export interface AuthMachineContext {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  error: Error | null;
}

export interface AuthPublicState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  profile: UserProfile | null;
  error: Error | null;
}

type StateTransition = {
  [K in AuthState]?: {
    [E in AuthEventType]?: AuthState;
  };
};

export const stateTransitions: StateTransition = {
  [AuthState.UNAUTHENTICATED]: {
    [AuthEventType.LOGIN]: AuthState.AUTHENTICATING,
  },
  [AuthState.AUTHENTICATING]: {
    [AuthEventType.LOGIN_SUCCESS]: AuthState.AUTHENTICATED,
    [AuthEventType.LOGIN_FAILED]: AuthState.UNAUTHENTICATED,
  },
  [AuthState.AUTHENTICATED]: {
    [AuthEventType.LOGOUT]: AuthState.DEAUTHENTICATING,
  },
  [AuthState.DEAUTHENTICATING]: {
    [AuthEventType.LOGOUT_SUCCESS]: AuthState.UNAUTHENTICATED,
  },
  [AuthState.ERROR]: {
    [AuthEventType.LOGIN]: AuthState.AUTHENTICATING,
  },
};

/**
 * Look up the next state for a (state, event) pair. Returns null when the
 * current state has no transition defined for the event.
 */
export function getNextState(currentState: AuthState, eventType: AuthEventType): AuthState | null {
  return stateTransitions[currentState]?.[eventType] || null;
}
